import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { api } from '@/lib/api.js'
import { qk } from '@/lib/query-keys.js'
import { useFormatters } from '@/lib/hooks.js'

/**
 * A small "Created by X · Last edited by Y" line for one record. Drop into
 * any detail page that has an id for something the activity log tracks —
 * see lib/activity-log.ts on the server for the full list of entity types.
 *
 * Silent when there is nothing to say: a record with no logged history
 * (it predates this feature, or the query is still loading) renders
 * nothing rather than an empty or loading placeholder, since this is a
 * footnote, not content anyone is waiting on.
 */
export function ActivityInfo({ entityType, entityId }: { entityType: string; entityId: string }) {
  const { t } = useTranslation()
  const formatters = useFormatters()

  const { data } = useQuery({
    queryKey: qk.activitySummary(entityType, entityId),
    queryFn: () => api.activity.summary(entityType, entityId),
    staleTime: 30 * 1000,
  })

  if (!data || (!data.createdBy && !data.lastEditedBy)) return null

  const sameEvent =
    data.createdBy &&
    data.lastEditedBy &&
    data.createdBy.staffUsername === data.lastEditedBy.staffUsername &&
    data.createdBy.at === data.lastEditedBy.at

  return (
    <p className="text-body-sm text-on-surface-variant">
      {data.createdBy && (
        <span>{t('activity.createdBy', { name: data.createdBy.staffDisplayName })}</span>
      )}
      {data.createdBy && data.lastEditedBy && !sameEvent && <span> · </span>}
      {data.lastEditedBy && !sameEvent && (
        <span>
          {t('activity.lastEditedBy', { name: data.lastEditedBy.staffDisplayName })}
          {' '}
          <span title={formatters.dateTime(data.lastEditedBy.at)}>
            ({formatters.dateTime(data.lastEditedBy.at)})
          </span>
        </span>
      )}
    </p>
  )
}
